<script setup>
import {useHead} from '@vueuse/head'
import HomeHeader from '@/components/HomeHeader.vue'
import Footer from '@/components/Footer.vue'
import {useI18n} from '@/i18n'

const {t} = useI18n()

useHead({
  title: () => t('marketing.meta.aboutTitle'),
  meta: [{name: 'description', content: () => t('marketing.meta.aboutDescription')}]
})
</script>

<template>
  <div class="page mkt-page mkt-about-page">
    <HomeHeader/>
    <main>
      <section class="mkt-about-hero">
        <img src="/images/about_1.jpg" alt="TPA Housing factory in Foshan">
        <div class="mkt-about-hero-shade"></div>
        <div class="mkt-shell mkt-about-hero-inner">
          <nav class="mkt-breadcrumb" aria-label="Breadcrumb">
            <a href="/">{{ t('marketing.shared.home') }}</a><i class="mdi mdi-chevron-right"></i><span>{{ t('marketing.shared.about') }}</span>
          </nav>
          <div>
            <span class="mkt-eyebrow is-light">{{ t('marketing.about.hero.eyebrow') }}</span>
            <h1>{{ t('marketing.about.hero.title') }}</h1>
            <p>{{ t('marketing.about.hero.text') }}</p>
          </div>
        </div>
      </section>

      <section class="mkt-stats is-about" aria-label="Company facts">
        <div class="mkt-shell mkt-stats-grid">
          <div v-for="stat in t('marketing.home.stats')" :key="stat.label" class="mkt-stat">
            <strong>{{ stat.value }}</strong><span>{{ stat.label }}</span>
          </div>
        </div>
      </section>

      <section class="mkt-section mkt-about-story">
        <div class="mkt-shell mkt-about-story-layout">
          <div class="mkt-about-story-copy">
            <span class="mkt-eyebrow">{{ t('marketing.about.story.eyebrow') }}</span>
            <h2>{{ t('marketing.about.story.title') }}</h2>
            <p>{{ t('marketing.about.story.p1') }}</p>
            <p>{{ t('marketing.about.story.p2') }}</p>
            <a class="mkt-button mkt-button-primary" href="/projects">{{ t('marketing.shared.viewProjects') }}</a>
          </div>
          <div class="mkt-about-story-media">
            <img class="is-main" src="/images/mining/index_factory1_22231.jpg" alt="Steel fabrication workshop" loading="lazy">
            <img class="is-detail" src="/images/mining/index_factory3_22233.jpg" alt="Automated steel production equipment" loading="lazy">
            <div><strong>18</strong><span>{{ t('static.about.years') }}</span></div>
          </div>
        </div>
      </section>

      <section class="mkt-vision-section">
        <div class="mkt-shell mkt-vision-layout">
          <div class="mkt-vision-image">
            <img src="/images/steelworks/video1.jpg" alt="TPA Housing steel structure team" loading="lazy">
          </div>
          <div class="mkt-vision-copy">
            <span class="mkt-eyebrow is-light">{{ t('marketing.about.vision.eyebrow') }}</span>
            <h2>{{ t('marketing.about.vision.title') }}</h2>
            <blockquote>{{ t('marketing.about.vision.text') }}</blockquote>
            <p>{{ t('marketing.about.vision.note') }}</p>
          </div>
        </div>
      </section>

      <section class="mkt-section mkt-about-capabilities">
        <div class="mkt-shell">
          <div class="mkt-section-heading">
            <div>
              <span class="mkt-eyebrow">{{ t('marketing.about.capabilities.eyebrow') }}</span>
              <h2>{{ t('marketing.about.capabilities.title') }}</h2>
            </div>
            <p>{{ t('marketing.about.capabilities.intro') }}</p>
          </div>
          <div class="mkt-capability-grid">
            <article v-for="capability in t('marketing.about.capabilities.items')" :key="capability.title">
              <i class="mdi" :class="capability.icon" aria-hidden="true"></i>
              <h3>{{ capability.title }}</h3>
              <p>{{ capability.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="mkt-section mkt-about-manufacturing">
        <div class="mkt-shell mkt-about-manufacturing-layout">
          <div class="mkt-about-manufacturing-copy">
            <span class="mkt-eyebrow">{{ t('marketing.about.manufacturing.eyebrow') }}</span>
            <h2>{{ t('marketing.about.manufacturing.title') }}</h2>
            <p>{{ t('marketing.about.manufacturing.text') }}</p>
            <ul class="mkt-check-list">
              <li v-for="point in t('marketing.about.manufacturing.points')" :key="point"><i class="mdi mdi-check"></i>{{ point }}</li>
            </ul>
          </div>
          <div class="mkt-about-manufacturing-gallery">
            <img src="/images/mining/index_factory2_22232.jpg" alt="Steel plate cutting workshop" loading="lazy">
            <img src="/images/mining/index_factory4_22234.jpg" alt="Steel component fabrication" loading="lazy">
            <img src="/images/mining/index_factory3_22233.jpg" alt="H beam automated production" loading="lazy">
          </div>
        </div>
      </section>

      <section class="mkt-global-section">
        <div class="mkt-global-image"><img src="/images/engineering/slider_1.jpg" alt="International steel building project" loading="lazy"></div>
        <div class="mkt-global-copy">
          <span class="mkt-eyebrow is-light">{{ t('marketing.about.global.eyebrow') }}</span>
          <h2>{{ t('marketing.about.global.title') }}</h2>
          <p>{{ t('marketing.about.global.text') }}</p>
          <div class="mkt-global-numbers">
            <div><strong>18</strong><span>{{ t('static.about.years') }}</span></div>
            <div><strong>60</strong><span>{{ t('marketing.home.stats')[5].label }}</span></div>
          </div>
        </div>
      </section>

      <section class="mkt-section mkt-service-chain">
        <div class="mkt-shell">
          <div class="mkt-section-heading">
            <div>
              <span class="mkt-eyebrow">{{ t('marketing.about.milestones.eyebrow') }}</span>
              <h2>{{ t('marketing.about.milestones.title') }}</h2>
            </div>
          </div>
          <ol>
            <li v-for="(item, index) in t('marketing.about.milestones.items')" :key="item">
              <span>{{ String(index + 1).padStart(2, '0') }}</span><strong>{{ item }}</strong>
            </li>
          </ol>
        </div>
      </section>

      <section class="mkt-about-cta">
        <div class="mkt-shell">
          <div>
            <span class="mkt-eyebrow is-light">{{ t('marketing.about.cta.eyebrow') }}</span>
            <h2>{{ t('marketing.about.cta.title') }}</h2>
            <p>{{ t('marketing.about.cta.text') }}</p>
          </div>
          <a class="mkt-button mkt-button-accent" href="/contact">{{ t('marketing.shared.quote') }}<i class="mdi mdi-arrow-right"></i></a>
        </div>
      </section>
    </main>
    <Footer/>
  </div>
</template>
